import { useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";

const STORE_KEY = "amora_quiz_state";

const loadState = () => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return { step: 0, answers: {} };
};

export default function Quiz({ quiz, final, onDownload }) {
  const steps = quiz.steps || [];
  const total = steps.length + 1; // + final screen
  const [{ step, answers }, setState] = useState(loadState);
  const containerRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ step, answers }));
    } catch (e) {}
  }, [step, answers]);

  const setStep = (s) => setState((p) => ({ ...p, step: Math.max(0, Math.min(total - 1, s)) }));
  const setAnswer = (idx, value) => setState((p) => ({ ...p, answers: { ...p.answers, [idx]: value } }));

  const next = () => setStep(step + 1);
  const back = () => setStep(step - 1);

  const handleSingle = (idx, opt) => {
    setAnswer(idx, opt);
    setTimeout(() => setStep(step + 1), 250);
  };

  const buildLead = () => ({
    gender: answers[0] || null,
    age: answers[1] || null,
    interests: Array.isArray(answers[2]) ? answers[2] : [],
    goal: answers[3] || null,
  });

  // keyboard: Esc -> back
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && step > 0) back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const isFinal = step >= steps.length;
  const cur = steps[step];
  const progress = Math.round(((step + 1) / total) * 100);
  const progressText = (quiz.progress_text || "Шаг {x} из {n}")
    .replace("{x}", String(step + 1))
    .replace("{n}", String(total));

  return (
    <div className="glass" style={{ padding: "clamp(1.3rem, 4vw, 2.4rem)" }} data-testid="quiz-card">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 min-h-[36px]">
        {step > 0 ? (
          <button className="btn-ghost px-4 py-2 text-sm flex items-center gap-1" onClick={back} data-testid="quiz-back-btn">
            <ArrowLeft size={16} /> {quiz.back_text?.replace("← ", "") || "Назад"}
          </button>
        ) : (
          <span />
        )}
        <span className="text-sm" style={{ color: "var(--text-sec)" }} data-testid="quiz-progress-text">
          {progressText}
        </span>
      </div>

      {/* Progress */}
      <div className="progress-track mb-8">
        <div className="progress-fill" style={{ width: `${progress}%` }} data-testid="quiz-progress-bar" />
      </div>

      <div ref={containerRef} key={step} className="step-anim">
        {!isFinal && cur && (
          <>
            <h3 className="font-head text-2xl sm:text-3xl font-bold mb-6" data-testid="quiz-question">
              {cur.question}
            </h3>

            {cur.type === "single" && (
              <div className="flex flex-col gap-3" data-testid="quiz-single-options">
                {(cur.options || []).map((opt) => (
                  <button
                    key={opt}
                    className={`opt ${answers[step] === opt ? "active" : ""}`}
                    onClick={() => handleSingle(step, opt)}
                    data-testid={`quiz-option-${opt}`}
                  >
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            )}

            {cur.type === "range" && (
              <RangeStep
                min={cur.min || 18}
                max={cur.max || 65}
                value={answers[step] || Math.round(((cur.min || 18) + (cur.max || 65)) / 2)}
                onChange={(v) => setAnswer(step, v)}
                onNext={next}
              />
            )}

            {cur.type === "multi" && (
              <MultiStep
                options={cur.options || []}
                selected={Array.isArray(answers[step]) ? answers[step] : []}
                onChange={(vals) => setAnswer(step, vals)}
                onNext={next}
                nextText={cur.next_text || "Далее →"}
              />
            )}
          </>
        )}

        {isFinal && (
          <div className="text-center py-4" data-testid="quiz-final">
            <div className="text-5xl mb-2" aria-hidden="true">✨</div>
            <h3 className="font-head text-2xl sm:text-3xl font-bold mb-3">{final.title}</h3>
            <p className="mb-8 max-w-md mx-auto" style={{ color: "var(--text-sec)" }}>
              {final.subtitle}
            </p>
            <button
              className="btn-grad cta-pulse px-9 py-4 text-lg w-full sm:w-auto"
              onClick={() => onDownload(buildLead())}
              data-testid="quiz-download-btn"
            >
              {final.download_button}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RangeStep({ min, max, value, onChange, onNext }) {
  return (
    <div data-testid="quiz-range">
      <div className="text-center mb-8">
        <span className="font-head text-6xl font-bold grad-text" data-testid="quiz-age-value">
          {value}
        </span>
        <span className="text-2xl ml-2" style={{ color: "var(--text-sec)" }}>
          лет
        </span>
      </div>
      <input
        type="range"
        className="amora-range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid="quiz-age-slider"
      />
      <div className="flex justify-between text-xs mt-2 mb-8" style={{ color: "var(--text-sec)" }}>
        <span>{min}</span>
        <span>{max}</span>
      </div>
      <button className="btn-grad px-8 py-3.5 w-full" onClick={onNext} data-testid="quiz-range-next-btn">
        Далее →
      </button>
    </div>
  );
}

function MultiStep({ options, selected, onChange, onNext, nextText }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((o) => o !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div data-testid="quiz-multi">
      <div className="flex flex-wrap gap-3 mb-8">
        {options.map((opt) => (
          <button
            key={opt}
            className={`chip ${selected.includes(opt) ? "active" : ""}`}
            onClick={() => toggle(opt)}
            data-testid={`quiz-chip-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
      <button
        className="btn-grad px-8 py-3.5 w-full disabled:opacity-50"
        onClick={onNext}
        disabled={selected.length === 0}
        data-testid="quiz-multi-next-btn"
      >
        {nextText}
      </button>
    </div>
  );
}
